/**
 * Centralized TanStack Query key factory for the AC-1377 React Query migration.
 *
 * Implements the conventions documented in
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4 and 5.1.
 *
 * ## Why a factory (and not stringly-typed keys at call sites)
 *
 * Every `useQuery({ queryKey: [...] })` and `invalidateQueries({ queryKey: [...] })`
 * call must agree on the exact array shape, or the cache silently splits into
 * disjoint entries and we ship stale-modal-data bugs (see AC-1390 / AC-1391 /
 * AC-1394 for the class of bugs this migration closes). Centralizing the keys
 * here gives us:
 *
 *   1. A single place where the resource taxonomy lives.
 *   2. Consistent ID coercion (see below) so router-string IDs and store-number
 *      IDs hit the same cache entry.
 *   3. Compile-time TS narrowing via `as const`, so invalidation predicates and
 *      `useQuery` generic inference work without manual casts.
 *
 * ## ID coercion rule
 *
 * - Workflow IDs are coerced via `Number()`. Workflow IDs are integers in the
 *   database; route params arrive as strings (`useParams<{ id: string }>()`)
 *   and store IDs are numbers. Coercing to number in the key makes
 *   `queryKeys.workflow('1')` and `queryKeys.workflow(1)` equal arrays so
 *   they share a cache entry.
 *
 * - Project IDs are coerced via `String()`. The TD chose string here because
 *   project IDs flow through React Router params (string) into far more code
 *   paths than workflow IDs do, and round-tripping back to string keeps the
 *   key stable across that boundary. (Functionally either choice works as
 *   long as it is *consistent* — what matters is the same input always
 *   produces the same key.)
 *
 * Either way, callers MUST go through this factory and never hand-craft
 * `['workflow', someId]` literals at call sites — that bypasses coercion and
 * reintroduces the cache-miss class of bug.
 *
 * ## Hierarchical invalidation contract
 *
 * Workflow keys are nested so that prefix invalidation cascades:
 *
 *   queryKeys.workflow(1)          === ['workflow', 1]
 *   queryKeys.workflowSteps(1)     === ['workflow', 1, 'steps']
 *   queryKeys.workflowExecution(1) === ['workflow', 1, 'execution']
 *
 * A single `queryClient.invalidateQueries({ queryKey: queryKeys.workflow(1) })`
 * (no `exact: true` flag) will mark the workflow detail, its steps, and its
 * execution data all stale in one call. This is RQ's default prefix-matching
 * behavior on array keys. Do NOT flatten these to sibling keys
 * (e.g. `['workflow-steps', id]`) without also reworking every caller's
 * invalidation strategy — the prefix relationship is load-bearing.
 *
 * The `workflows()` list key is intentionally a different first element from
 * `workflow(id)` so that invalidating the list does not refetch every
 * individual workflow detail (and vice versa).
 *
 * ## Adding a new key
 *
 * 1. Add a new factory function to the `queryKeys` object below.
 * 2. Use `as const` on the returned array literal so TS narrows the tuple.
 * 3. Coerce IDs explicitly with `Number()` (numeric primary keys) or
 *    `String()` (string primary keys / scoping IDs that flow through routes).
 * 4. If the new key is hierarchical under an existing resource, nest it
 *    (`['resource', id, 'subresource']`) so existing invalidation cascades
 *    apply.
 * 5. Update the unit tests in
 *    `frontend/src/hooks/queries/__tests__/queryKeys.test.ts` to cover the
 *    new factory's shape and coercion.
 *
 * Do NOT update CLAUDE.md from this ticket — the user-facing convention doc
 * lives in AC-1415 and ships separately.
 */

export const queryKeys = {
  /** Detail key for a single workflow. */
  workflow: (id: string | number) => ['workflow', Number(id)] as const,

  /** Steps for a single workflow. Hierarchical under workflow(id). */
  workflowSteps: (id: string | number) =>
    ['workflow', Number(id), 'steps'] as const,

  /** Execution data for a single workflow. Hierarchical under workflow(id). */
  workflowExecution: (id: string | number) =>
    ['workflow', Number(id), 'execution'] as const,

  /** All-workflows list (intentionally a sibling of `workflow`, not a prefix). */
  workflows: () => ['workflows'] as const,

  /**
   * AC-1599: Projects list key. Currently projects also live in
   * `useProjectStore` (Zustand); this key is reserved for hybrid invalidation:
   * clone/delete mutations call `useProjectStore.getState().loadProjects()`
   * AND invalidate this key so any future React Query consumer of projects
   * gets fresh data automatically. Mirrors the `workflows()` shape (sibling
   * of any per-project key, not a prefix) so list-level invalidation does
   * not refetch every per-project resource.
   */
  projects: () => ['projects'] as const,

  /** Agents in a project. */
  projectAgents: (projectId: string | number) =>
    ['project', String(projectId), 'agents'] as const,

  /** Models in a project. */
  projectModels: (projectId: string | number) =>
    ['project', String(projectId), 'models'] as const,

  /** Registry-enabled models in a project. */
  projectRegistries: (projectId: string | number) =>
    ['project', String(projectId), 'registries'] as const,

  /**
   * AC-1478: Detail key for a queue (queue-scoped resources hang off this).
   *
   * Numeric coercion follows the same rule as `workflow(id)`: queue IDs are
   * integer primary keys but route params arrive as strings, so we coerce to
   * `Number()` to keep `'42'` and `42` in the same cache slot.
   */
  queue: (queueId: string | number) => ['queue', Number(queueId)] as const,

  /**
   * AC-1479: All-queues list key. Sibling of `queue(id)` (different first
   * element) so list-level invalidation does not refetch every individual
   * queue detail and vice versa. Mirrors the workflow / workflows split.
   */
  queues: () => ['queues'] as const,

  /**
   * AC-1478: Priority histogram for a queue, keyed by status filter and bin
   * count. Status is part of the key so that switching the chip routes to a
   * different cache slot (PENDING and PROCESSING data must not collapse).
   * Hierarchical under `queue(queueId)` so a single
   * `invalidateQueries(queryKeys.queue(id))` cascades to the histogram.
   */
  queuePriorityHistogram: (
    queueId: string | number,
    status: string,
    bins: number,
  ) =>
    [
      'queue',
      Number(queueId),
      'priority-histogram',
      status,
      bins,
    ] as const,

  /**
   * AC-1479: Payload-field discovery for a queue. Hierarchical under
   * `queue(queueId)` so a single `invalidateQueries(queryKeys.queue(id))`
   * cascades to refresh the discovery list (e.g. after a queue mutation
   * that might affect what's displayed).
   */
  queuePayloadFields: (queueId: string | number) =>
    ['queue', Number(queueId), 'payload-fields'] as const,

  /**
   * AC-1515 (epic AC-1493): The current user's per-provider API key list
   * returned by `GET /api/v1/credentials`. The list includes one row per
   * supported provider — including providers the user has NOT configured
   * yet — each carrying a `has_key` flag plus non-secret metadata
   * (display_label, last_validated_at, created_at).
   *
   * No nesting/coercion: the list is user-scoped (auth-derived), so no ID
   * threads through the key.
   */
  apiKeys: () => ['apiKeys'] as const,

  /**
   * AC-1515: Per-provider detail key. Hierarchical under `apiKeys()` so a
   * `invalidateQueries({ queryKey: queryKeys.apiKeys() })` cascades to any
   * provider-specific subscribers if/when they're added. The provider
   * string is the key segment (already canonical from `LLMProvider`).
   */
  apiKey: (provider: string) => ['apiKeys', provider] as const,

  /**
   * AC-1493 P6: LLM catalog response (`{ byok_mode, models }`). Cached
   * here so mutations on user API keys (AC-1515) can invalidate the
   * derived `available` flags atomically.
   */
  llmCatalog: () => ['llmCatalog'] as const,

  /**
   * AC-1616 (epic AC-1609): The key-gated assistant model allow-list
   * (`GET /api/v1/assistant/models` -> `{ models, default }`). User-scoped
   * (auth-derived) and not nested under any ID, so the key is a flat
   * single-segment array — same shape as `apiKeys()` / `llmCatalog()`.
   */
  assistantModels: () => ['assistantModels'] as const,

  /**
   * AC-1616: The current user's saved assistant model preference
   * (`GET /api/v1/assistant/model-preference` -> `{ model }`). Sibling of
   * `assistantModels()` (different first element) so the set-preference
   * mutation can invalidate the preference without refetching the
   * allow-list. User-scoped, so no ID threads through the key.
   */
  assistantModelPreference: () => ['assistantModelPreference'] as const,

  /**
   * AC-1619: Deployment runtime mode (`GET /api/v1/system/mode` ->
   * `{ byok_mode, supported_providers }`). Deployment-scoped (not user- or
   * ID-scoped), so the key is a flat single-segment array — same shape as
   * `apiKeys()` / `llmCatalog()`. The API Keys settings page reads this to
   * decide whether per-user keys are honored (BYOK) or platform-managed (F&F).
   */
  systemMode: () => ['systemMode'] as const,

  /**
   * AC-1738 (epic AC-1732): MCP servers list for the integrations UI. The
   * assistant-driven provisioning flow (confirm/decline a proposed remote
   * server) invalidates this key on success so a newly provisioned server
   * surfaces in the integrations list without a manual refetch. Flat
   * single-segment array; the integrations read is project-scoped at the API
   * layer, so prefix-invalidating `['mcpServers']` refreshes any subscriber.
   */
  mcpServers: () => ['mcpServers'] as const,

  /**
   * AC-1746 (defect AC-1743, epic AC-1732): per-built-in credential status (e.g.
   * the Email built-in, id -2). The in-chat configure flow
   * (`useConfirmBuiltinSetup` / `useDeclineBuiltinSetup`) invalidates this key on
   * success so any "is this built-in configured?" read re-runs after the user
   * connects (or declines) the built-in from the assistant chat. The server id is
   * part of the key so distinct built-ins do not collide in the cache.
   */
  builtinCredentials: (builtinServerId: number) =>
    ['builtinCredentials', builtinServerId] as const,

  /**
   * AC-1790 (epic AC-1787): the schedule for a single workflow (one schedule per
   * orchestration). The in-chat concierge schedule-setup flow
   * (`useConfirmScheduleSetup` / `useDeclineScheduleSetup`) invalidates this key
   * on success so any ScheduleConfigPanel / ScheduleIndicator subscriber re-reads
   * after the user confirms (or declines) the recurring schedule from the
   * assistant chat. The orchestration id is part of the key so distinct
   * workflows' schedules do not collide in the cache. Workflow ids are coerced
   * with Number() per the ID-coercion rule.
   */
  schedule: (orchestrationId: number | string) =>
    ['schedule', Number(orchestrationId)] as const,
} as const;
